import React, { useState } from "react";
import { useForm } from "react-hook-form";
import BackDrop from "../../../components/ui/BackDrop";
import { cn } from "../../../utils/twMerge";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { closeIcon } from "../../../assetData/icons";
import CustomDateInput from "../../../components/ui/custom-date-input/CustomDateInput";
import MirageLoader from "../../../components/ui/pageLoader/MirageLoader";

const MasterPriceEdit = ({ buttonConfig, selectedItemId, isButtonClicked }) => {
  const getOneHandler = buttonConfig.getOneHandler;
  const editHandler = buttonConfig.editHandler;
  const closeHandler = buttonConfig.closeEditModal;

  const [handleValue, handleSetValue] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [initialValue1, setInitialValue1] = useState("");

  const schema = z.object({
    item_description: z.string().nonempty(),
    short_name: z.string().nonempty(),
    unit_price: z.string().nonempty(),
    item_type: z.string(),
    status: z.string(),
    eff_start_date: z.string(),
    eff_end_date: z.string(),
  });

  const form = useForm({
    defaultValues: async () => {
      const data = await getOneHandler(selectedItemId);
      setInitialValue(data.eff_start_date);
      setInitialValue1(data.eff_end_date);

      return {
        item_description: data.item_description,
        short_name: data.short_name,
        unit_price: data.unit_price,
        item_type: data.item_type,
        status: data.active_status,
        eff_start_date: data.eff_start_date,
        eff_end_date: data.eff_end_date,
        updated_by: data.updated_by,
      };
    },
    resolver: zodResolver(schema),
  });

  const errors = form.formState.errors;

  const onSubmit = async (data) => {
    const statusValue = form.getValues("status") === "true";
    const updatedData = { ...data, status: statusValue };

    const res = await editHandler(selectedItemId, updatedData);
    if (res) closeHandler();
  };

  const onClose = () => {
    closeHandler();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  return (
    <>
      <div className="fixed left-1/2 z-50 mx-auto min-h-[300px] w-[98%] max-w-[1000px] -translate-x-1/2 rounded bg-white p-8 max-lg:top-[10%] lg:top-1/2 lg:-translate-y-1/2">
        <h2 className="pb-4 text-2xl font-bold text-neutral-800">
          Edit Pricing
        </h2>

        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} />
        </button>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="max-md:scrollbar-custom w-full overflow-x-hidden pr-2 max-lg:max-h-[550px] max-md:overflow-y-scroll md:overflow-y-hidden lg:max-h-[400px] ">
            {/* row 1 */}

            <div className="w-full items-baseline gap-2 lg:flex ">
              <div className="w-full items-baseline gap-2 sm:flex max-sm:[&>div]:mt-4 ">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Item description
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("item_description")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.description && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">Short Name</label>
                  <input
                    placeholder="Type here"
                    {...form.register("short_name")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.short_name && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
              <div className=" w-full items-stretch gap-4 text-neutral-600 max-lg:mt-4 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">Unit Price</label>
                  <input
                    {...form.register("unit_price")}
                    type="number"
                    step="any"
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.unit_price && "border-[1px] border-red-300",
                    )}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={handleKey}
                  />
                </div>
                <div className={cn("flex w-full flex-col  gap-2")}></div>
              </div>
            </div>

            {/* row 2 */}
            <div className="mt-8 w-full items-end gap-2 max-lg:mt-4 lg:flex ">
              <div className="w-full items-end gap-2 max-lg:mt-4 sm:flex ">
                {" "}
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">Item Type</label>
                  <select
                    {...form.register("item_type")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.item_type && "border-[1px] border-red-300",
                    )}
                    defaultValue={form?.formState?.defaultValues?.item_type}
                  >
                    <option value="license">License</option>
                    <option value="Storage">Storage</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                <div className={cn("flex w-full flex-col gap-2 max-sm:mt-4")}>
                  <label className="text-base text-[#636363]">
                    Active Status
                  </label>
                  <select
                    {...form.register("status")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.status && "border-[1px] border-red-300",
                    )}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="w-full items-end gap-2 max-lg:mt-4 sm:flex ">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Eff Start Date
                  </label>
                  <CustomDateInput
                    max={form.getValues("eff_end_date")}
                    handleSetValue={handleSetValue}
                    initialValue={initialValue}
                    form1={form.register("eff_start_date")}
                    className="w-full rounded-[4px] bg-neutral-100"
                  />
                </div>

                <div className={cn("flex w-full flex-col gap-2 max-sm:mt-4")}>
                  <label className="text-base text-[#636363]">
                    Eff End Date
                  </label>
                  <CustomDateInput
                    form1={form.register("eff_end_date")}
                    handleSetValue={handleSetValue}
                    handleValue={handleValue}
                    initialValue={initialValue1}
                    min={handleValue}
                    className="w-full rounded-[4px] bg-neutral-100"
                  />
                </div>
              </div>
            </div>

            {/* row 3 */}
            <div className="items-end gap-2 max-lg:mt-4 sm:flex lg:w-[50%] ">
              <div className={cn("flex w-full flex-col  gap-2")}>
                <label className="text-base text-[#636363]">
                  Last Updated By
                </label>
                <input
                  {...form.register("updated_by")}
                  className={cn(
                    "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                  )}
                />
              </div>
              <div className={cn("flex w-full flex-col  gap-2")}></div>
            </div>
          </div>

          {/* row 4 */}
          <div className="flex items-end max-sm:justify-center md:justify-between  max-lg:[&>button]:w-1/2 max-sm:[&>button]:w-full lg:[&>button]:w-1/4">
            <button
              type="submit"
              disabled={isButtonClicked}
              className={` ${
                isButtonClicked ? "opacity-[70%]" : ""
              } mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-2 py-2 font-semibold text-white `}
            >
              {isButtonClicked ? <MirageLoader /> : "SAVE CHANGES"}
            </button>
            <button className="max-lg:hidden"></button>
            <button className="max-lg:hidden"></button>
          </div>
        </form>
      </div>
      <BackDrop />
    </>
  );
};

export default MasterPriceEdit;
